import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
  ref,
  createRef,
} from '@hh/global.js'

class HHProductGalleryZoom extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
      converter: {
        fromAttribute: (value) => value === 'true',
        toAttribute: (value) => (value ? 'true' : 'false'),
      },
    },
  }

  $scrollContainer = createRef()

  constructor() {
    super()
    this.open = false
    this.targetMediaId = null
    this.preloadLinks = []
    this.onDocumentKeyDown = this.onDocumentKeyDown.bind(this)
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  updated(changedProperties) {
    if (!changedProperties.has('open')) return

    this.toggleModalLock()
    this.toggleEscapeListener(!!this.open)

    if (this.open) {
      this.hydrateImages()
      this.scrollToTarget()
      this.preloadAdjacent()
    } else {
      this.clearPreloads()
    }
  }

  connectedCallback() {
    super.connectedCallback()
    this.open = false

    this.receiver = broadcastReceiver('gallery-zoom', (...args) => {
      this.openFromDocument(...args)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.receiver?.disconnect()
    this.toggleEscapeListener(false)
    this.clearPreloads()
    this.unfreezeBody()
    document.body.style.overflowY = ''
  }

  openFromDocument(data) {
    if (String(this.id) !== String(data.id)) return

    this.targetMediaId = data.mediaId
    document.body.style.overflowY = 'hidden'

    if (this.open) {
      this.hydrateImages()
      this.scrollToTarget()
      this.preloadAdjacent()
      return
    }

    this.open = true
  }

  toggleEscapeListener(enable) {
    document.removeEventListener('keydown', this.onDocumentKeyDown)
    if (enable) {
      document.addEventListener('keydown', this.onDocumentKeyDown)
    }
  }

  onDocumentKeyDown(event) {
    if (!this.open) return
    if (event.code?.toUpperCase() !== 'ESCAPE') return
    event.preventDefault()
    this.close()
  }

  close() {
    this.open = false
    document.body.style.overflowY = ''
  }

  toggleModalLock() {
    if (this.open) {
      this.freezeBody()
    } else {
      this.unfreezeBody()
    }
  }

  freezeBody() {
    const html = document.documentElement
    html.classList.add('frozen')
    document.body.classList.add('frozen')
    html.style.scrollbarGutter = 'stable'
  }

  unfreezeBody() {
    const html = document.documentElement
    html.classList.remove('frozen')
    document.body.classList.remove('frozen')
    html.style.scrollbarGutter = ''
  }

  hydrateImages() {
    const targetId = `zoom-${this.targetMediaId}`

    this.querySelectorAll('.gallery-zoom__figure').forEach((figure) => {
      const img = figure.querySelector('img[data-src]')
      if (!img) return

      if (figure.id === targetId) {
        img.loading = 'eager'
        img.fetchPriority = 'high'
      }

      if (img.dataset.srcset) img.srcset = img.dataset.srcset
      img.src = img.dataset.src
      delete img.dataset.srcset
      delete img.dataset.src
    })
  }

  scrollToTarget() {
    const mediaId = this.targetMediaId
    if (mediaId == null) return

    requestAnimationFrame(() => {
      const figure = this.querySelector(`#zoom-${mediaId}`)
      if (!figure) return
      if (this.scrollToFocalPoint(figure)) return

      figure.scrollIntoView({ block: 'start' })
    })
  }

  scrollToFocalPoint(figure) {
    const container = this.$scrollContainer.value
    if (!container) return false

    const img = figure.querySelector('img')
    const percent = parseFloat(img?.dataset.focalPointY ?? '')
    if (!Number.isFinite(percent)) return false

    // The width/height attributes reserve the box before the source loads, so
    // the rect is measurable on the first frame.
    const imgRect = img.getBoundingClientRect()
    if (!imgRect.height) return false

    const containerRect = container.getBoundingClientRect()
    const imgTop = imgRect.top - containerRect.top + container.scrollTop
    const focalTop = imgTop + imgRect.height * (percent / 100)
    const maxScroll = container.scrollHeight - container.clientHeight

    container.scrollTop = Math.min(
      Math.max(focalTop - container.clientHeight / 2, 0),
      Math.max(maxScroll, 0)
    )

    return true
  }

  preloadAdjacent() {
    this.clearPreloads()

    const figures = [...this.querySelectorAll('.gallery-zoom__figure')]
    const currentIndex = figures.findIndex(
      (figure) => figure.id === `zoom-${this.targetMediaId}`
    )
    if (currentIndex < 0) return

    const indexes = new Set()
    for (let offset = -2; offset <= 2; offset++) {
      const index = currentIndex + offset
      if (index >= 0 && index < figures.length) indexes.add(index)
    }

    indexes.forEach((index) => {
      const img = figures[index].querySelector('img')
      if (!img) return

      if (index === currentIndex) {
        img.loading = 'eager'
        img.fetchPriority = 'high'
      }

      const link = document.createElement('link')
      link.rel = 'preload'
      link.as = 'image'

      if (img.srcset) {
        // Mirror srcset/sizes so the preload resolves to the same candidate the
        // img picks. currentSrc is empty until the browser resolves it, so
        // reading it here would fall back to the original-width src.
        link.setAttribute('imagesrcset', img.srcset)
        link.setAttribute('imagesizes', img.sizes || '100vw')
      } else {
        const href = img.currentSrc || img.src
        if (!href) return
        link.href = href
      }

      document.head.appendChild(link)
      this.preloadLinks.push(link)
    })
  }

  clearPreloads() {
    this.preloadLinks.forEach((link) => link.remove())
    this.preloadLinks = []
  }

  render() {
    return html`
      <div class="overlay"></div>
      <div
        ${ref(this.$scrollContainer)}
        class="content"
        role="dialog"
        aria-modal="true"
        aria-label="${t('products.modal.label')}"
        tabindex="-1"
      >
        <hh-focus-trap active="${this.open}">
          <button
            @click="${this.close}"
            class="button"
            type="button"
            aria-label="${t('accessibility.close')}"
          >
            ${unsafeHTML(paths.close)}
          </button>
          <div class="gallery" role="document">
            <slot></slot>
          </div>
        </hh-focus-trap>
      </div>
    `
  }

  static styles = css`
    :host {
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      inset: 0;
      width: 100%;
      visibility: hidden;
      content-visibility: hidden;
      pointer-events: none;
      z-index: -1;
      transition: opacity 0.25s linear;
    }

    .overlay {
      width: 100%;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      z-index: 1;
      background-color: #2e2a2b80;
      pointer-events: none;
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      content-visibility: visible;
      pointer-events: auto;
      z-index: 1000;
    }

    .button {
      width: 4rem;
      height: 4rem;
      position: fixed;
      top: 2.5rem;
      right: 2.5rem;
      display: flex;
      background-color: var(--hh-color-background);
      border: 0.1rem solid var(--hh-color-light);
      border-radius: 50%;
      color: var(--hh-color-foreground);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      margin: 0;
      padding: 0;
      z-index: 2;
    }

    .button svg {
      width: 1.4rem;
      height: 1.4rem;
      pointer-events: none;
    }

    .content {
      position: relative;
      z-index: 1;
      display: flex;
      flex-direction: column;
      height: 100vh;
      height: 100dvh;
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
      width: 100%;
    }

    hh-focus-trap {
      display: block;
      min-height: 100%;
    }

    .gallery {
      width: 100%;
    }

    ::slotted(.gallery-zoom__figure) {
      width: 100%;
      margin: 0;
    }

    ::slotted(.gallery-zoom__figure) img,
    ::slotted(.gallery-zoom__figure .img) {
      width: 100%;
      height: auto;
      display: block;
    }
  `
}

customElements.define('hh-product-gallery-zoom', HHProductGalleryZoom)
